import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { ChangeList, useImprovements, type NewProblem } from '../components/Improvements.js';
import { useLocale, usePlanState } from '../components/site.js';
import { LinkButton, LoadError, Loading } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];
const DAY_MS = 86_400_000;

/**
 * Open problems first seen in the last `days` days, one per rule and without hints (decision 3 of
 * SP-UX10.4): from the list the problem page uses; titles and severity only (T-101).
 */
function useNewProblems(siteId: string, days: number, enabled: boolean) {
  const locale = useLocale();
  const open = useQuery({
    queryKey: ['site', siteId, 'findings', 'open', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/findings', {
          params: { path: { siteId }, query: { status: 'open', limit: 200, locale } },
        }),
      ),
    enabled,
  });
  const since = Date.now() - days * DAY_MS;
  const seen = new Set<string>();
  const news: NewProblem[] = [];
  for (const f of open.data?.items ?? []) {
    if (f.severity === 'info' || Date.parse(f.firstSeenAt) < since || seen.has(f.ruleCode))
      continue;
    seen.add(f.ruleCode);
    news.push(f);
  }
  // Only the first page of the open problems came: there may be more new ones (review #183).
  const partial = open.data ? open.data.total > open.data.items.length : false;
  return { ...open, news, partial };
}

/**
 * What got better (T-36, site-changes.html): how many problems were fixed and how many are new in
 * the period, the good news with what they bring, and the new problems. Periods beyond the
 * history of the plan are locked, as the API would cut them silently (decision 4 of SP-UX10.4).
 */
export function Changes() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const factsId = useId();
  const { plan, settled } = usePlanState();
  const history = plan?.limits.historyDays;
  const allowed = (d: Period) => history === undefined || d <= history;
  const [picked, setPicked] = useState<Period | null>(null);
  // 30 days first; 7 when the plan did not load – every plan has them, the API cuts the others.
  const wanted = picked ?? (settled && history === undefined ? 7 : 30);
  // Never a period the plan does not show: the longest one it does instead.
  const days = allowed(wanted) ? wanted : ([...PERIODS].reverse().find(allowed) ?? 7);
  // Asked only once the plan is known: a longer period the API would cut to the plan's silently.
  const feed = useImprovements(siteId, days, settled);
  const fresh = useNewProblems(siteId, days, settled);
  const items = feed.data?.items ?? [];
  const fixed = items.filter((i) => i.kind === 'fixed').length;
  const locked = PERIODS.some((d) => !allowed(d));

  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('changes.title')}</h1>
          <div className="sub">
            <span>{t('changes.intro')}</span>
          </div>
        </div>
        <div className="seg" role="group" aria-label={t('changes.period')}>
          {PERIODS.map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={days === d}
              disabled={!allowed(d)}
              onClick={() => setPicked(d)}
            >
              {t('changes.days', { count: d })}
              {allowed(d) ? null : <span className="sr-only"> – {t('availability.locked')}</span>}
            </button>
          ))}
        </div>
      </div>
      {locked ? (
        <div className="upsell">
          <span>{t('site.chart.locked')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('availability.plans')}
          </LinkButton>
        </div>
      ) : null}
      {feed.isPending || (items.length === 0 && fresh.isPending) ? <Loading /> : null}
      {feed.isError ? (
        <LoadError message={errorMessage(feed.error, t)} onRetry={() => void feed.refetch()} />
      ) : null}
      {/* Not before the new problems came: they would replace "nothing" a moment later. */}
      {feed.data && !fresh.isPending && items.length === 0 && fresh.news.length === 0 ? (
        <div className="empty">
          <b>{t('changes.empty')}</b>
          <span>{t('changes.emptyText')}</span>
        </div>
      ) : null}
      {feed.data && (items.length > 0 || fresh.news.length > 0) ? (
        <section className="card" aria-labelledby={factsId}>
          <h2 id={factsId} className="sr-only">
            {t('changes.title')}
          </h2>
          <dl className="kv row">
            <div>
              <dt>{t('changes.fixedIn', { count: days })}</dt>
              <dd className="mono">{fixed}</dd>
            </div>
            <div>
              <dt>{t('changes.newCount')}</dt>
              <dd className="mono">
                {fresh.data
                  ? fresh.partial
                    ? t('changes.atLeast', { count: fresh.news.length })
                    : fresh.news.length
                  : '–'}
              </dd>
            </div>
          </dl>
          <ChangeList siteId={siteId} items={items} news={fresh.news} timeZone={timeZone} />
        </section>
      ) : null}
    </>
  );
}
